import { BootstrapApi } from './bootstrap/BootstrapApi';
import { Errors } from './Errors';
import { Translation } from './models/Translation';
import { GlobalSettings } from './models/GlobalSettings';
import { AnonymousSettings } from './models/AnonymousSettings';
import { PlayerController } from '../Business/player/PlayerController';
import { CachedQuoteApi } from './price/CachedQuoteApi';
import { CachedBookmarkApi } from './user/CachedBookmarkApi';
import { CachedFavoriteApi } from './user/CachedFavoriteApi';
import { CachedChannelApi } from './CachedChannelApi';
import { CachedRecordingsApi } from './user/CachedRecordingsApi';
import { CachedChannelRightsApi } from './CachedChannelRightsApi';
import { UnauthorizedHandler } from './UnauthorizedHandler';
import App from '../App';
import { TokenData } from './bootstrap/TokenData';
import { IBootstrapConfig, IDeviceProfileElementV1, ILoginSessionV3, IMenu } from './models/AndromeTV.types';
import { AnalyticsService } from './analytics/AnalyticsService';
import { ErrorResponse } from './models/ErrorResponse';
import { CachedNpvrSettings } from './user/CachedNpvrSettings';
import { Log, Router } from '@lightningjs/sdk';
import { GlobalSettingsKeys } from './models/GlobalSettingsKeys';
import { CachedCustomerSettings } from './user/CachedCustomerSettings';

/**
 * @property {string} locationURL
 * @property {string} environment
 * @property {string} imageURL
 * @property {string} thumbnailURL
 * @property {string} [bearer]
 * @property {Translation} translations
 * @property {GlobalSettings} globalSettings
 * @property {Translation} genres
 * @property {AnonymousSettings} anonymousSettings
 * @property {PlayerController} playerController
 * @property {CachedQuoteApi} cachedQuoteApi
 * @property {CachedBookmarkApi} cachedBookmarkApi
 * @property {ILoginSessionV3} [sessionInfo]
 * @property {IDeviceProfileElementV1} deviceProfile
 * @property {TokenData} [_token]
 * @property {IMenu} menu
 * @property {AnalyticsService} logService
 * @property {string} locale
 */
export class Backend {
  private _appRoot: App;
  locationURL!: string;
  environment!: string;
  imageURL!: string;
  thumbnailURL!: string;
  bearer: string | undefined;
  translations!: Translation;
  globalSettings!: GlobalSettings;
  genres!: Translation;
  anonymousSettings!: AnonymousSettings;
  playerController!: PlayerController;
  cachedQuoteApi!: CachedQuoteApi;
  cachedBookmarkApi!: CachedBookmarkApi;
  cachedFavoriteApi!: CachedFavoriteApi;
  cachedChannelRightsApi!: CachedChannelRightsApi;
  cachedChannelApi!: CachedChannelApi;
  cachedRecordingsApi!: CachedRecordingsApi;
  unauthorizedHandler!: UnauthorizedHandler;
  logService!: AnalyticsService;
  locale!: string;
  rootPage = 'login';

  private _token: TokenData | undefined;
  sessionInfo?: ILoginSessionV3;
  deviceProfile?: IDeviceProfileElementV1;
  menu?: IMenu;
  cachedNpvrSettings!: CachedNpvrSettings;
  cachedCustomerSettings: CachedCustomerSettings;

  serverTimeCheckPassed = false;
  private static _instance: Backend;

  constructor(appRoot: App) {
    this._appRoot = appRoot;
    Backend._instance = this;
  }

  static get instance(): Backend {
    return Backend._instance;
  }

  /**
   * Initializes the backend and performs the standard initialization calls
   */
  initialize(): Promise<Backend> {
    return BootstrapApi.discover(this)
      .then(data => {
        this.locale = 'en-US';
        this.locationURL = data.location;
        this.environment = data.enviroment;
        this.imageURL = data.imageLocation;
        this.thumbnailURL = data.thumbnailLocation;
        this.bearer = undefined;
        this.translations = new Translation([]);
        this.globalSettings = new GlobalSettings([]);
        this.genres = new Translation([]);
        this.anonymousSettings = new AnonymousSettings();
        this.playerController = new PlayerController(this, this._appRoot);
        this.unauthorizedHandler = new UnauthorizedHandler();
        return BootstrapApi.defaultConfig(this);
      })
      .then(response => {
        this.extractConfig(response);
        return BootstrapApi.config(this);
      })
      .then(response => {
        this.extractConfig(response);
        // only initialize the cached apis and logservice after the globalsettings have been parsed.
        this.cachedQuoteApi = new CachedQuoteApi(this);
        this.cachedBookmarkApi = new CachedBookmarkApi(this);
        this.cachedFavoriteApi = new CachedFavoriteApi(this);
        this.cachedChannelRightsApi = new CachedChannelRightsApi(this);
        this.cachedChannelApi = new CachedChannelApi(this);
        this.cachedRecordingsApi = new CachedRecordingsApi(this);
        this.logService = new AnalyticsService(this);
        this.cachedNpvrSettings = new CachedNpvrSettings(this);
        this.cachedCustomerSettings = new CachedCustomerSettings(this);
        return BootstrapApi.timeRequest(this);
      })
      .then(response => {
        const serverTime = +response.time * 1000;
        const deviceTime = Date.now();
        Log.info('Backend server time:', serverTime, deviceTime);
        const maxDifference = this.globalSettings.numberValue(GlobalSettingsKeys.startupTimeDifference) || 120000;
        // displaying of warning message is handled in the main component/page
        this.serverTimeCheckPassed = Math.abs(serverTime - deviceTime) < maxDifference;
        return this;
      })
      .catch(error => {
        if (!this.translations) {
          this.extractConfig(BootstrapApi.defaultConfig(this));
        }
        throw error;
      });
  }

  reload(): Promise<Backend> {
    return BootstrapApi.discover(this)
      .then(data => {
        this.locale = 'en-US';
        this.locationURL = data.location;
        this.environment = data.enviroment;
        this.imageURL = data.imageLocation;
        this.thumbnailURL = data.thumbnailLocation;
        return BootstrapApi.defaultConfig(this);
      })
      .then(response => {
        this.extractConfig(response);
        return BootstrapApi.config(this);
      })
      .then(response => {
        this.extractConfig(response);
        return BootstrapApi.timeRequest(this);
      })
      .then(response => {
        const serverTime = +response.time * 1000;
        const deviceTime = Date.now();
        Log.info('Backend server time:', serverTime, deviceTime);
        const maxDifference = this.globalSettings.numberValue(GlobalSettingsKeys.startupTimeDifference) || 120000;
        // displaying of warning message is handled in the main component/page
        this.serverTimeCheckPassed = Math.abs(serverTime - deviceTime) < maxDifference;
        return this;
      })
      .catch(error => {
        if (!this.translations) {
          this.extractConfig(BootstrapApi.defaultConfig(this));
        }
        throw error;
      });
  }

  /**
   * Create an url to the api server
   */
  makeUrl(url: string, queryParam: any = {}): string {
    const params = [];
    for (const [key, value] of Object.entries(queryParam)) {
      if (value instanceof Array) {
        value.forEach(val => {
          if (val != null) {
            params.push(`${key}=${encodeURIComponent(val as string | boolean | number)}`);
          }
        });
      } else if (value != null) {
        params.push(`${key}=${encodeURIComponent(value as string | boolean | number)}`);
      }
    }
    const paramString = params.join('&');
    return paramString.length > 0 ? `${this.locationURL}/${url}?${paramString}` : `${this.locationURL}/${url}`;
  }

  /**
   * Store the token data in the backend object
   */
  set token(val: TokenData | undefined) {
    this._token = val;
    this.bearer = val ? `Bearer ${val.access?.token}` : undefined; // todo TS-check doublecheck the ?
  }

  /**
   * Extract values from config response and store them in our local maps
   */
  extractConfig(config: IBootstrapConfig) {
    // todo TS-check appTranslations might return undefined, set this.x to undefined?
    if (config.appTranslations) this.translations = new Translation(config.appTranslations);
    if (config.globalSettings) this.globalSettings = new GlobalSettings(config.globalSettings);
    if (config.genreTranslations) this.genres = new Translation(config.genreTranslations);
    this.anonymousSettings = new AnonymousSettings(config.anonymousConfig);
    this.locale = config.locale;
  }

  clearSession() {
    this.token = undefined;
    this.sessionInfo = undefined;
    this.deviceProfile = undefined;
    this.menu = undefined;
    this._clearCaches();
  }

  _clearCaches() {
    this.cachedBookmarkApi.clearCache();
    this.cachedChannelApi.clearCache();
    this.cachedChannelRightsApi.clearCache();
    this.cachedNpvrSettings.clearCache();
    this.cachedCustomerSettings.clearCache();
    this.cachedFavoriteApi.clearCache();
    this.cachedQuoteApi.clearCache();
    this.cachedRecordingsApi.clearCache();
  }

  /**
   * Retrieve json data from the backend
   */
  getJson(url: string, bearer?: string): Promise<unknown> {
    const headers: HeadersInit = {
      Accept: 'application/vnd.nebula-v2+json,application/json',
      'Content-Type': 'application/json',
    };
    if (bearer) {
      headers['Authorization'] = bearer;
    }
    const fetch_param: RequestInit = {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: headers,
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
    };

    return this.doRequest(url, fetch_param).catch(err => {
      return this.retryRequest(err, url, fetch_param);
    });
  }

  /**
   * post data to the url in form encode format
   */
  postForm(url: string, data: { [key: string]: string | number | boolean }): Promise<Response> {
    const formBody = [];
    for (const [key, value] of Object.entries(data)) {
      const encodedKey = encodeURIComponent(key);
      const encodedValue = encodeURIComponent(value);
      formBody.push(encodedKey + '=' + encodedValue);
    }
    return fetch(url, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      body: formBody.join('&'),
    }).then(response => {
      if (!response.ok) {
        return response.json().then(
          data => {
            if (data.error) {
              throw new CustomError(`${data.error}`, data);
            }
            throw new Error(`HTTP_ERROR_${response.status}`);
          },
          err => {
            Log.warn('Error converting response to json', err);
            throw new Error(`HTTP_ERROR_${response.status}`);
          }
        );
      }
      return response.json();
    });
  }

  /**
   * post json data to the specified url
   */
  postJson(url: string, data: unknown, bearer?: string): Promise<unknown> {
    const headers: HeadersInit = {
      Accept: 'application/vnd.nebula-v2+json,application/json',
      'Content-Type': 'application/vnd.nebula-v2+json',
    };
    if (bearer) {
      headers['Authorization'] = bearer;
    }

    const postParam: RequestInit = {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: headers,
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify(data),
    };

    return this.doRequest(url, postParam).catch(err => {
      return this.retryRequest(err, url, postParam);
    });
  }

  /**
   * perform a delete request to the backend
   */
  delete(url: string, bearer?: string): Promise<unknown> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (bearer) {
      headers['Authorization'] = bearer;
    }
    const params: RequestInit = {
      method: 'DELETE',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: headers,
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
    };

    return this.doRequest(url, params).catch(err => {
      return this.retryRequest(err, url, params);
    });
  }

  /**
   * put json data to the specified url
   */
  putJson(url: string, data: unknown, bearer?: string): Promise<unknown> {
    const headers: HeadersInit = {
      'Content-Type': 'application/vnd.nebula-v2+json',
    };
    if (bearer) {
      headers['Authorization'] = bearer;
    }

    const param: RequestInit = {
      method: 'PUT',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: headers,
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify(data),
    };

    return this.doRequest(url, param).catch(err => {
      return this.retryRequest(err, url, param);
    });
  }

  isAuthorized(): boolean {
    return !!this.bearer; //TODO implement isauthorized
  }

  doRequest(url: string, params: RequestInit) {
    return fetch(url, params).then(response => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new ErrorResponse(data, response.status);
        });
      }
      return response.status !== 204 ? response.json() : {};
    });
  }

  goToLogin() {
    Router.navigate('login');
  }

  retryRequest(err: { code: string }, url: string, params: RequestInit) {
    if (err.code === Errors.Unauthorized) {
      return this.unauthorizedHandler.handle(this).then(retry => {
        if (retry && this.bearer) {
          params.headers = {
            ...params.headers,
            Authorization: this.bearer,
          };
          return this.doRequest(url, params);
        }
      });
    } else {
      throw err;
    }
  }
}

export class CustomError extends Error {
  private data: unknown | undefined;
  constructor(message?: string, data?: unknown) {
    super(message);
    Object.setPrototypeOf(this, CustomError.prototype);
    this.data = data;
  }
}
